/**
 * Ingenico 5000F (GMP3) Fiscal Device Integration
 * Ingenico 5000F Yazarkasa Entegrasyonu
 * 
 * BatchSale modu ile tek HTTP çağrısında tüm satış işlemini tamamlar:
 * Start -> OptionFlags -> TicketHeader -> ItemSale(tüm ürünler) -> Payment -> Close
 */

import {
  IFiscalDevice,
  FiscalProductItem,
  FiscalOperationResult,
  FiscalApiResponse,
  MultiplePosPayment,
  PaymentStepCallback,
} from './types';

const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';
let currentLocalServiceUrl = DEFAULT_LOCAL_SERVICE_URL;

export const setLocalServiceUrl = (url: string) => {
  if (url && url !== currentLocalServiceUrl) {
    console.log('[Ingenico 5000F] Yerel servis URL güncellendi:', url);
    currentLocalServiceUrl = url;
  }
};

const getApiBaseUrl = () => `${currentLocalServiceUrl}/api/yazarkasa`;

const fetchWithFallback = async (url: string, options: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (primaryError) {
    if (currentLocalServiceUrl !== DEFAULT_LOCAL_SERVICE_URL) {
      const fallbackUrl = url.replace(currentLocalServiceUrl, DEFAULT_LOCAL_SERVICE_URL);
      console.warn(`[LocalService] Birincil URL başarısız (${currentLocalServiceUrl}), fallback deneniyor: ${DEFAULT_LOCAL_SERVICE_URL}`);
      return await fetch(fallbackUrl, options);
    }
    throw primaryError;
  }
};

const INGENICO_ERROR_MESSAGES: Record<number, { title: string; message: string }> = {
  0x0001: { title: 'İşleme İzin Verilmedi', message: 'Yazarkasa bu işleme izin vermedi. Lütfen cihaz ayarlarını kontrol edin.' },
  0x0002: { title: 'Zaman Aşımı', message: 'Yazarkasa yanıt vermedi (zaman aşımı). Lütfen cihazın açık ve bağlı olduğundan emin olun.' },
  0x0004: { title: 'İşlem İptal Edildi', message: 'İşlem cihaz üzerinden iptal edildi.' },
  0x0008: { title: 'EKÜ Hatası', message: 'Elektronik Kayıt Ünitesinde sorun oluştu. Lütfen teknik servise başvurun.' },
  0x0020: { title: 'Kağıt Bitti', message: 'Yazarkasada kağıt bitmiş. Lütfen yeni kağıt rulosu takın ve işlemi tekrar deneyin.' },
  0xE001: { title: 'Kağıt Bitti', message: 'Yazarkasada kağıt bitmiş. Lütfen yeni kağıt rulosu takın ve işlemi tekrar deneyin.' },
  0xF01C: { title: 'Cihaz Meşgul', message: 'Yazarkasa şu anda meşgul. Lütfen birkaç saniye bekleyip tekrar deneyin.' },
  2001: { title: 'Ürün Zaten İptal Edilmiş', message: 'Bu ürün daha önce iptal edilmiş, tekrar iptal edilemez.' },
  2010: { title: 'KDV Oranı Bulunamadı', message: 'Ürünün KDV oranı yazarkasada tanımlı değil. Lütfen departman ayarlarını kontrol edin.' },
  2011: { title: 'Fiş Limiti Aşıldı', message: 'Fiş tutarı yazarkasanın izin verdiği limiti aşıyor. Lütfen sepeti bölerek işlem yapın.' },
  2012: { title: 'Ürün Limiti Aşıldı', message: 'Bir fişte izin verilen maksimum ürün sayısına ulaşıldı. Lütfen sepeti bölerek işlem yapın.' },
  2016: { title: 'Kısım Fiyat Limiti Aşıldı', message: 'Departman satış limiti aşıldı. Lütfen sepeti bölerek işlem yapın.' },
  2021: { title: 'Ödeme Zaten Yapılmış', message: 'Bu fiş için ödeme zaten alınmış. Yeni bir fiş başlatın.' },
  2022: { title: 'Tutar Hatası', message: 'Ödeme tutarı kalan tutardan fazla olamaz.' },
  2031: { title: 'Cihaz Kapatılmış', message: 'Yazarkasa geçici olarak kapatılmış. Lütfen cihazı açın ve tekrar deneyin.' },
  2046: { title: 'Ödeme Tamamlanmalı', message: 'Açık bir fiş var ve ödemenin tamamlanması gerekiyor. Lütfen kalan tutarı ödeyin veya fişi iptal edin.' },
  2047: { title: 'Fatura Limiti Aşıldı', message: 'Fatura limiti aşıldı. Lütfen sepeti bölerek işlem yapın.' },
  2051: { title: 'Cihaz Kapalı', message: 'Yazarkasa kapalı. Lütfen cihazı açın ve tekrar deneyin.' },
  2053: { title: 'Kasiyer Girişi Gerekli', message: 'Yazarkasada kasiyer girişi yapılması gerekiyor. Lütfen cihaz üzerinden giriş yapın.' },
  2055: { title: 'Parametre Yüklenmemiş', message: 'Yazarkasa parametreleri yüklenmemiş. Lütfen cihaz üzerinden parametre yükleyin.' },
  2060: { title: 'Ödeme Reddedildi', message: 'Ödeme yazarkasa tarafından kabul edilmedi. Lütfen farklı bir ödeme yöntemi deneyin.' },
  2063: { title: 'Maksimum Deneme Aşıldı', message: 'Maksimum deneme sayısına ulaşıldı. Lütfen bir süre bekleyip tekrar deneyin.' },
  2066: { title: 'Ürün Fiyatı Bulunamadı', message: 'Ürün fiyatı bulunamadı. Lütfen ürün bilgilerini kontrol edin.' },
  2067: { title: 'Fiş Limiti Aşılamaz', message: 'Toplam tutar mali fiş limitini (12.000 TL) aşıyor. Lütfen "Sepet Bölme" özelliğini kullanarak işlemi parçalayın.' },
  2068: { title: 'Ürün Bulunamadı', message: 'Fişe ürün eklenmemiş. Lütfen en az bir ürün ekleyin.' },
  2069: { title: 'Önce Ödemeleri İptal Edin', message: 'Fişte ödeme bulundu. Fişi iptal etmeden önce tüm ödemelerin iptal edilmesi gerekiyor.' },
  2080: { title: 'İşlem Zaten Yapılmış', message: 'Bu işlem daha önce tamamlanmış. Yeni bir işlem başlatın.' },
  2085: { title: 'Ödeme Başarısız', message: 'Ödeme alınamadı. Kart reddedilmiş olabilir. Lütfen farklı bir kart deneyin veya nakit ödeme yapın.' },
  2086: { title: 'Ödeme Başarısız', message: 'Ödeme alınamadı. Banka tarafından reddedildi. Lütfen farklı bir kart deneyin.' },
  2087: { title: 'Geçersiz Ödeme Tipi', message: 'Seçilen ödeme tipi bu yazarkasa tarafından desteklenmiyor.' },
  2097: { title: 'Açık İşlem Var', message: 'Cihazda tamamlanmamış bir işlem bulunuyor. Lütfen önce "Belgeyi İptal Et" butonuna basın.' },
  2104: { title: 'Ethernet Hatası', message: 'Yazarkasanın ethernet modülü çalışmıyor. Lütfen kablo bağlantısını kontrol edin.' },
  2105: { title: 'Ethernet Kablosu Takılı Değil', message: 'Yazarkasanın ethernet kablosu takılı değil. Lütfen kablo bağlantısını kontrol edin.' },
  61472: { title: 'Eşleştirme Gerekli', message: 'Yazarkasa ile eşleştirme süresi dolmuş. Otomatik yeniden eşleştirme deneniyor... Eğer sorun devam ederse yazarkasa kontrol ekranından yeniden eşleştirme yapın.' },
};

function getIngenicoFriendlyError(errorCode: number | undefined, rawMessage: string): { title: string; message: string } {
  if (errorCode !== undefined && INGENICO_ERROR_MESSAGES[errorCode]) {
    return INGENICO_ERROR_MESSAGES[errorCode];
  }

  if (rawMessage.includes('TRAN_RESULT_NO_PAPER') || rawMessage.includes('NO_PAPER')) {
    return INGENICO_ERROR_MESSAGES[0x0020];
  }
  if (rawMessage.includes('RECV_BUSY') || rawMessage.includes('mesgul')) {
    return INGENICO_ERROR_MESSAGES[0xF01C];
  }
  if (rawMessage.includes('TIMEOUT') || rawMessage.includes('timeout')) {
    return INGENICO_ERROR_MESSAGES[0x0002];
  }
  if (rawMessage.includes('USER_ABORT')) {
    return INGENICO_ERROR_MESSAGES[0x0004];
  }
  if (rawMessage.includes('PAYMENT_NOT_SUCCESSFUL')) {
    return INGENICO_ERROR_MESSAGES[2085];
  }
  if (rawMessage.includes('FIS_LIMITI')) {
    return INGENICO_ERROR_MESSAGES[2067];
  }

  return {
    title: 'Yazarkasa Hatası',
    message: rawMessage || 'Bilinmeyen bir hata oluştu. Lütfen tekrar deneyin.',
  };
}

const DLL_RETCODE_PAIRING_REQUIRED = 61472; // 0xF020

/**
 * Kurulum ekranının (yazarkasa-kurulum.html) kaydettiği Ingenico ayarları.
 * GTS-Web, Electron ana penceresinde kurulum/kontrol ekranlarıyla aynı
 * localStorage origin'ini paylaştığı için bu kayıt buradan da okunabilir.
 */
interface StoredIngenicoSettings {
  ip?: string;
  port?: number;
  setupCompleted?: boolean;
  pairingBrand?: string;
  pairingModel?: string;
  pairingSerial?: string;
}

const getStoredIngenicoSettings = (): StoredIngenicoSettings | null => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('yazarkasaSettings_ingenico');
    return raw ? (JSON.parse(raw) as StoredIngenicoSettings) : null;
  } catch {
    return null;
  }
};

/**
 * Echo dener. DIKKAT: Echo yalnizca FIZIKSEL baglantiyi dogrular;
 * eslestirme (pairing) ancak mali komutlarda (Start/BatchSale) sinanir.
 * Echo basarili olsa bile satis komutu 61472 (pairing required) donebilir.
 */
const tryEcho = async (): Promise<boolean> => {
  try {
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-echo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(15000),
    });
    const result = await resp.json();
    return result.success === true;
  } catch {
    return false;
  }
};

/**
 * Mevcut interface'i kaldirir (FP3_RemoveInterface).
 * KRITIK: Cihaz GMP3 portunda tek TCP istemcisi kabul eder. Eski interface
 * kaldirilmadan yeni CreateInterface yapilirsa eski baglanti hatti tutmaya
 * devam eder ve yeni interface'in tum komutlari 61443 TIMEOUT alir
 * (gmp3-reconnect'in guvenilir olmasinin sebebi de once Remove yapmasidir).
 */
const disconnectInterface = async (): Promise<void> => {
  try {
    await fetchWithFallback(`${getApiBaseUrl()}/gmp3-disconnect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(10000),
    });
  } catch {
    // Disconnect hatasi kritik degil; CreateInterface yine de denenir.
  }
};

/**
 * Kurulum kaydındaki IP/port ile CreateInterface yapar; StartPairing'i
 * YALNIZCA gerekiyorsa çalıştırır (bağlantı sonrası Echo başarısızsa).
 * Echo yalnızca eşleştirilmiş oturumda çalıştığı için başarısı pairing'in
 * hâlâ geçerli olduğunu kanıtlar — böylece her kopmada cihazda gereksiz
 * eşleştirme tetiklenmez (kontrol ekranı ve main.js ile aynı akış).
 * forcePairing=true: cihaz 61472 (pairing required) döndüyse Echo kontrolü
 * atlanır, doğrudan eşleştirme yapılır.
 */
const reconnectFromStoredSettings = async (forcePairing: boolean): Promise<boolean> => {
  const settings = getStoredIngenicoSettings();
  if (!settings?.setupCompleted || !settings.ip || !settings.port) {
    console.warn('[Ingenico 5000F] Kurulum kaydı bulunamadı, ayarlardan yeniden bağlanılamıyor.');
    return false;
  }
  try {
    console.log(`[Ingenico 5000F] Kurulum kaydından yeniden bağlanılıyor: ${settings.ip}:${settings.port}`);
    // Once eski interface'i kaldir - kaldirilmazsa cihazin tek TCP hattini
    // eski interface tutar ve yeni interface 61443 TIMEOUT alir.
    await disconnectInterface();
    // Timeout geniş: DLL, CreateInterface içinde kendi TCP retry'larını yapar (3 x 10sn);
    // cihaz standa yeni oturduğunda bağlantı 15sn'den uzun sürebilir.
    const connResp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ip: settings.ip, port: settings.port }),
      signal: AbortSignal.timeout(45000),
    });
    const connResult = await connResp.json();
    if (!connResult.success) {
      console.warn('[Ingenico 5000F] CreateInterface başarısız:', connResult.message || connResult.errorMessage);
      return false;
    }

    if (!forcePairing && (await tryEcho())) {
      console.log('[Ingenico 5000F] Echo başarılı - eşleştirme hâlâ geçerli, StartPairing atlanıyor.');
      return true;
    }

    // Eşleştirme kimliği tüm ekranlarla ortak (kurulum kaydından, yoksa backend varsayılanları)
    console.log('[Ingenico 5000F] StartPairing yapılıyor...');
    const pairResp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-pair`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        externalBrand: settings.pairingBrand || 'GTS',
        externalModel: settings.pairingModel || 'AQUASOFT',
        externalSerial: settings.pairingSerial || 'GTS-POS-001',
      }),
      signal: AbortSignal.timeout(30000),
    });
    const pairResult = await pairResp.json();
    if (!pairResult.success) {
      console.warn('[Ingenico 5000F] StartPairing başarısız:', pairResult.message || pairResult.errorMessage);
      return false;
    }
    console.log('[Ingenico 5000F] Kurulum kaydından yeniden bağlantı + eşleştirme başarılı!');
    return true;
  } catch (err: any) {
    console.error('[Ingenico 5000F] Ayarlardan yeniden bağlanma hatası:', err.message);
    return false;
  }
};

/** gmp3-reconnect: eski interface'i kaldirir + CreateInterface + StartPairing (sunucu tarafi). */
const serverSideReconnect = async (): Promise<boolean> => {
  try {
    console.log('[Ingenico 5000F] Server-side reconnect deneniyor (gmp3-reconnect)...');
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-reconnect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(45000),
    });
    const result = await resp.json();
    if (result.success) {
      console.log(`[Ingenico 5000F] Server-side reconnect başarılı! (${result.ip}:${result.port})`);
      return true;
    }
    console.warn('[Ingenico 5000F] Server-side reconnect başarısız:', result.message);
    return false;
  } catch (err: any) {
    console.error('[Ingenico 5000F] Server-side reconnect hatası:', err.message);
    return false;
  }
};

/**
 * Otomatik yeniden bağlanma.
 * - forcePairing=false (varsayılan; Echo başarısızlığı durumları): kurulum
 *   kaydından bağlanır, pairing'i YALNIZCA gerekiyorsa yapar. Böylece normal
 *   satış akışına asla gereksiz eşleştirme eklenmez.
 * - forcePairing=true (cihaz 61472 "pairing required" döndüğünde): eşleştirme
 *   CIHAZIN TALEBİ olduğu için zorunludur. Bu durumda en güvenilir yol olan
 *   gmp3-reconnect (eski interface'i kaldırır + bağlanır + eşleştirir) önce
 *   denenir; cihaz meşgulse (61443) kısa bekleyip kurulum kaydı yoluyla
 *   bir kez daha denenir.
 */
export const attemptAutoReconnect = async (forcePairing: boolean = false): Promise<boolean> => {
  if (forcePairing) {
    if (await serverSideReconnect()) return true;
    // Cihaz o an mesgul olabilir (StartPairing 61443) - kisa bekleyip tek retry
    console.log('[Ingenico 5000F] Reconnect başarısız, 2sn bekleyip tekrar deneniyor...');
    await new Promise((r) => setTimeout(r, 2000));
    return await reconnectFromStoredSettings(true);
  }

  const viaSettings = await reconnectFromStoredSettings(false);
  if (viaSettings) return true;
  return await serverSideReconnect();
};

const callGmp3API = async (
  endpoint: string,
  body: any,
  showAlertModal?: (title: string, message: string) => void,
  timeoutMs: number = 120000,
  _isRetry: boolean = false
): Promise<FiscalApiResponse> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetchWithFallback(`${getApiBaseUrl()}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));
      console.error(`[Ingenico 5000F] API error (${endpoint}):`, errorData);
      return { success: false, data: errorData, rawResponse: response };
    }

    const data = await response.json();

    if (data.success === false) {
      if (!_isRetry && data.errorCode === DLL_RETCODE_PAIRING_REQUIRED) {
        console.warn(`[Ingenico 5000F] Eşleştirme gerekli (${endpoint}). Otomatik yeniden bağlanılıyor...`);
        // Cihaz pairing istedi: forcePairing=true ile Echo kontrolü atlanır
        const reconnected = await attemptAutoReconnect(true);
        if (reconnected) {
          console.log(`[Ingenico 5000F] Yeniden bağlandı, ${endpoint} tekrar deneniyor...`);
          return callGmp3API(endpoint, body, showAlertModal, timeoutMs, true);
        }
      }

      console.error(`[Ingenico 5000F] İşlem hatası (${endpoint}):`, data);
      const rawMsg = data.message || data.errorMessage || 'Bilinmeyen hata';
      const friendly = getIngenicoFriendlyError(data.errorCode, rawMsg);

      return { success: false, data: { ...data, friendlyTitle: friendly.title, friendlyMessage: friendly.message }, rawResponse: response };
    }

    console.log(`[Ingenico 5000F] API success (${endpoint}):`, data);
    return { success: true, data, rawResponse: response };
  } catch (error: any) {
    console.error(`[Ingenico 5000F] API genel hata (${endpoint}):`, error);
    const isAbort = error.name === 'AbortError';
    const title = isAbort ? 'Zaman Aşımı' : 'Bağlantı Hatası';
    const msg = isAbort
      ? 'Yazarkasa yanıt vermedi (zaman aşımı). Cihaz çok yavaş yanıt veriyor olabilir. Lütfen cihazı kontrol edin ve tekrar deneyin.'
      : 'Yazarkasayla iletişim kurulamadı. Lütfen cihazın açık ve ağa bağlı olduğundan emin olun.';
    return { success: false, data: { message: error.message, friendlyTitle: title, friendlyMessage: msg }, rawResponse: null };
  }
};

/**
 * ✅ İLETİŞİM HATASI SONRASI GetTicket DOĞRULAMASI
 * HTTP isteği timeout/ağ hatası ile sonuçlandığında (yanıt hiç alınamadığında) cihazda
 * ödemenin gerçekten alınıp alınmadığını CheckOpenTransaction (Json_FP3_GetTicket) ile tespit eder.
 * - completed: Ödeme cihazda tamamlanmış → fiş gmp3-recover ile kapatılır, satış BAŞARILI sayılmalı
 * - partial:   Kısmi ödeme var → kullanıcıya kalan tutar bildirilmeli
 * - none:      Açık işlem var ama ödeme görünmüyor → fişe DOKUNULMAZ, karar kullanıcıya bırakılır
 * - unknown:   Cihaz durumu doğrulanamadı (açık işlem yok / erişilemedi) → kullanıcı kararı gerekli
 *
 * GMPSmartDLL resmî dokümanı (PaymentTimeout / DLL Reset akışları): timeout sonrası
 * GetTicket ile bilgiler alınır ve karar "Application Decision" olarak uygulamaya bırakılır.
 * Otomatik VoidAll yapılmaz — cihaz banka/EFT ekranındayken ödeme henüz fişe
 * yansımamış olabilir; o anda iptal edilirse alınan ödemenin fişi kaybedilir.
 */
export interface IngenicoPaymentVerification {
  status: 'completed' | 'partial' | 'none' | 'unknown';
  totalAmountTL?: number;
  totalPaymentTL?: number;
  remainingAmountTL?: number;
  /** Cihazda açık işlem var mı? (unknown durumunda "açık işlem var ama detay okunamadı" ayrımı için) */
  hasOpenTransaction?: boolean;
}

export async function verifyIngenicoPaymentAfterCommError(): Promise<IngenicoPaymentVerification> {
  try {
    console.warn('[Ingenico 5000F] 🔎 İletişim hatası sonrası GetTicket doğrulaması başlıyor...');
    // Not: local-service DLL çağrılarını serialize eder; devam eden batch işlemi varsa
    // bu istek onun bitmesini bekler. Bu yüzden zaman aşımı geniş tutulur.
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-check-open-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(150000),
    });
    const data = await resp.json();

    if (!data.success) {
      return { status: 'unknown' };
    }
    if (!data.hasOpenTransaction) {
      // Açık işlem yok: ödeme hiç alınmamış OLABİLİR ya da ödeme alınıp fiş kapanmış
      // OLABİLİR (yanıt bize ulaşamadı). Ayrım yapılamaz → kullanıcı kararı gerekli.
      console.warn('[Ingenico 5000F] 🔎 Doğrulama: cihazda açık işlem yok, durum belirsiz.');
      return { status: 'unknown', hasOpenTransaction: false };
    }

    const totalAmount = data.totalAmount || 0;
    const totalPayment = data.totalPayment || 0;
    const remaining = data.remainingAmount || 0;
    const base = {
      totalAmountTL: totalAmount / 100,
      totalPaymentTL: totalPayment / 100,
      remainingAmountTL: remaining / 100,
      hasOpenTransaction: true,
    };

    // ⚠️ KRİTİK: Açık işlem var ama fiş detayları OKUNAMADI (totalAmount=0 → broker
    // GetTicket hata verdi; cihaz büyük olasılıkla banka/EFT ödeme ekranında meşgul).
    // Fişe KESİNLİKLE dokunma — zorla iptal edersek cihazda gerçekte alınmış bir
    // ödemenin fişi kaybedilir. Kullanıcı cihaz müsait olunca tekrar sorgulamalı.
    if (!data.paymentsComplete && totalAmount <= 0) {
      console.warn('[Ingenico 5000F] 🔎 Doğrulama: açık işlem var ama detaylar okunamadı (cihaz meşgul olabilir). Fişe dokunulmuyor.');
      return { status: 'unknown', hasOpenTransaction: true };
    }

    if (data.paymentsComplete) {
      console.warn('[Ingenico 5000F] 🔎 Doğrulama: ödeme cihazda TAMAMLANMIŞ. Fiş kapatılıyor (recover)...');
      try {
        await fetchWithFallback(`${getApiBaseUrl()}/gmp3-recover`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          signal: AbortSignal.timeout(180000),
        });
      } catch (recErr: any) {
        console.error('[Ingenico 5000F] 🔎 Doğrulama sonrası fiş kapatma hatası (kurtarma sonraki işlemde denenecek):', recErr.message);
      }
      return { status: 'completed', ...base };
    }

    if (totalPayment > 0) {
      console.warn(`[Ingenico 5000F] 🔎 Doğrulama: KISMİ ödeme var (${totalPayment}/${totalAmount} kuruş).`);
      return { status: 'partial', ...base };
    }

    // Ödeme görünmüyor → ⚠️ OTOMATİK İPTAL YOK! Cihaz banka/EFT ekranındaysa ödeme
    // henüz fişe yansımamış olabilir; şimdi void edersek alınan ödemenin fişi kaybolur.
    // Doküman akışı (PaymentTimeout/DLL Reset): karar uygulamaya/kullanıcıya bırakılır.
    console.warn('[Ingenico 5000F] 🔎 Doğrulama: fişte ödeme GÖRÜNMÜYOR. Fişe dokunulmadı — karar kullanıcıya bırakılıyor.');
    return { status: 'none', ...base };
  } catch (err: any) {
    console.error('[Ingenico 5000F] 🔎 Doğrulama hatası:', err.message);
    return { status: 'unknown' };
  }
}

/**
 * ✅ Kullanıcı ONAYIYLA açık işlemi iptal et (recover).
 * verifyIngenicoPaymentAfterCommError 'none' döndüğünde otomatik çağrılMAZ;
 * kullanıcı cihaz ekranını kontrol edip "ödeme alınmadı" kararını verdikten sonra
 * modaldaki "Fişi İptal Et" butonu ile çağrılır.
 */
export async function voidIngenicoOpenTransaction(): Promise<boolean> {
  try {
    console.warn('[Ingenico 5000F] 🗑️ Kullanıcı onayıyla açık işlem iptal ediliyor (recover)...');
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-recover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(180000),
    });
    const data = await resp.json();
    console.warn('[Ingenico 5000F] 🗑️ İptal sonucu:', data?.action, data?.message);
    // 'voided_clean' → ödeme yoktu, fiş iptal edildi
    // 'payments_complete_close' → bu arada ödeme tamamlanmış, fiş KAPATILDI (iptal değil!)
    return !!data?.success;
  } catch (err: any) {
    console.error('[Ingenico 5000F] 🗑️ İptal hatası:', err.message);
    return false;
  }
}

/**
 * Ödeme hatası sonrası doğrulama yap ve sonucu FiscalOperationResult'a dönüştür.
 * Sadece HTTP yanıtı hiç alınamadığında (rawResponse === null) çağrılmalıdır;
 * yanıt alınabildiyse local-service zaten GetTicket doğrulamasını yapıp
 * paymentCompleted alanını dönmüştür.
 */
async function resolveFailureWithVerification(
  result: FiscalApiResponse,
  fallbackMessage: string
): Promise<FiscalOperationResult> {
  if (result.rawResponse === null) {
    const verification = await verifyIngenicoPaymentAfterCommError();
    if (verification.status === 'completed') {
      console.warn('[Ingenico 5000F] ✅ İletişim hatasına rağmen ödeme cihazda TAMAMLANMIŞ (GetTicket doğrulaması). Başarılı sayılıyor.');
      return { success: true };
    }
    if (verification.status === 'partial') {
      return {
        success: false,
        errorMessage:
          `Cihazda KISMİ ödeme tespit edildi: ${(verification.totalPaymentTL ?? 0).toFixed(2)} TL alınmış, ` +
          `${(verification.remainingAmountTL ?? 0).toFixed(2)} TL eksik. ` +
          `Kalan tutarı cihazdan tamamlayın veya işlemi cihazdan iptal edin.`,
      };
    }
    if (verification.status === 'none') {
      return {
        success: false,
        errorMessage:
          (result.data?.friendlyMessage || result.data?.message || fallbackMessage) +
          '\n\nCihaz kontrol edildi: fişte ödeme görünmüyor. Cihaz ekranını kontrol edin; ' +
          'ödeme alınmadıysa fiş otomatik temizlenip yeniden denenebilir.',
      };
    }
    return {
      success: false,
      errorMessage:
        (result.data?.friendlyMessage || result.data?.message || fallbackMessage) +
        '\n\n⚠️ Cihaz durumu DOĞRULANAMADI. Ödemenin alınıp alınmadığını cihazdan/POS slibinden kontrol edin. ' +
        'Ödeme alındıysa "Yine de Devam Et" ile satışı kaydedebilirsiniz.',
    };
  }
  return {
    success: false,
    errorMessage: result.data?.friendlyMessage || result.data?.message || fallbackMessage,
    errorCode: result.data?.errorCode,
  };
}

/**
 * FiscalProductItem[] -> GMP3 BatchSale items formatına dönüştür
 * price: kuruş cinsinden (TL * 100), quantity: x1000
 * deptIndex: Ingenico 0-based (ilk departman=0). GTS departmentNo 1-based olduğu için -1 uygulanır.
 * Dokümantasyon: "deptIndex must be 0 if you want to use first department"
 */
function toGmp3Items(products: FiscalProductItem[]) {
  return products.map(p => {
    const deptNo = p.departmentNo ?? 1;
    const deptIndex = Math.max(0, deptNo - 1);
    return {
      name: p.name.substring(0, 40),
      price: Math.round(p.price * (1 + p.vatRate / 100) * 100),
      quantity: Math.round(p.quantity * 1000),
      deptIndex,
    };
  });
}

/**
 * Toplam tutarı kuruş cinsine çevir
 */
function toKurus(amount: number): number {
  return Math.round(amount * 100);
}

export class Ingenico5000FDevice implements IFiscalDevice {
  /**
   * Nakit ödeme - BatchSale ile tek çağrıda tamamlanır
   */
  async processCashPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Ingenico 5000F] Nakit ödeme başlıyor...', { productCount: products.length, totalAmount });

      const items = toGmp3Items(products);
      const paymentAmount = toKurus(totalAmount);

      const result = await callGmp3API('gmp3-batch-sale', {
        items,
        paymentType: 1,
        paymentAmount,
        ticketType: 1,
      }, showAlertModal, 120000);

      if (!result.success) {
        // Timeout/hata olsa bile cihaz tarafında ödeme tamamlanmış olabilir; GetTicket
        // tabanlı paymentCompleted ile gerçek durumu doğrularız.
        if (result.data?.paymentCompleted === true) {
          console.warn('[Ingenico 5000F] Nakit ödeme hatası döndü ANCAK cihazda ödeme tamamlanmış (GetTicket). Başarılı sayılıyor.');
          return {
            success: true,
            documentId: result.data?.transactionHandle || undefined,
          };
        }
        // HTTP yanıtı hiç alınamadıysa (frontend timeout / ağ hatası) cihaz durumunu
        // GetTicket ile burada doğrula.
        return await resolveFailureWithVerification(result, 'Ingenico nakit satış başarısız.');
      }

      console.log('[Ingenico 5000F] Nakit ödeme başarılı:', result.data);

      return {
        success: true,
        documentId: result.data?.transactionHandle || undefined,
      };
    } catch (error: any) {
      console.error('[Ingenico 5000F] Nakit ödeme hatası:', error);
      return {
        success: false,
        errorMessage: error.message || 'Nakit ödeme sırasında beklenmeyen hata.',
      };
    }
  }

  /**
   * POS ödeme - BatchSale ile tek çağrıda (paymentType: 4 = kart)
   */
  async processPosPayment(
    products: FiscalProductItem[],
    totalAmount: number,
    showAlertModal?: (title: string, message: string) => void,
    cashAmount?: number
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Ingenico 5000F] POS ödeme başlıyor...', { productCount: products.length, totalAmount, cashAmount });

      const items = toGmp3Items(products);
      const paymentAmount = toKurus(totalAmount);

      const result = await callGmp3API('gmp3-batch-sale', {
        items,
        paymentType: 4,
        paymentAmount,
        ticketType: 1,
      }, showAlertModal, 120000);

      if (!result.success) {
        // Ödeme adımı timeout/hata verse bile cihaz tarafında kart çekilip fiş kesilmiş
        // olabilir. Local servis, GetTicket tabanlı RecoverTransaction ile gerçek durumu
        // tespit edip paymentCompleted alanını döner. Tamamlanmışsa BAŞARILI sayarız.
        if (result.data?.paymentCompleted === true) {
          console.warn('[Ingenico 5000F] POS ödeme hatası döndü ANCAK cihazda ödeme tamamlanmış (GetTicket). Başarılı sayılıyor.', {
            totalAmount: result.data?.totalAmount,
            totalPayment: result.data?.totalPayment,
          });
          return {
            success: true,
            documentId: result.data?.transactionHandle || undefined,
            fiscalData: {
              FiscalTransactionId: result.data?.transactionHandle || null,
            },
          };
        }
        // HTTP yanıtı hiç alınamadıysa (frontend timeout / ağ hatası) cihaz durumunu
        // GetTicket ile burada doğrula.
        return await resolveFailureWithVerification(result, 'Ingenico POS satış başarısız.');
      }

      console.log('[Ingenico 5000F] POS ödeme başarılı:', result.data);

      return {
        success: true,
        documentId: result.data?.transactionHandle || undefined,
        fiscalData: {
          FiscalTransactionId: result.data?.transactionHandle || null,
        },
      };
    } catch (error: any) {
      console.error('[Ingenico 5000F] POS ödeme hatası:', error);
      return {
        success: false,
        errorMessage: error.message || 'POS ödeme sırasında beklenmeyen hata.',
      };
    }
  }

  /**
   * Çoklu ödeme - Her ödeme tipi için ayrı BatchSale
   * GMP3 BatchSale tek ödeme destekler, bu yüzden çoklu POS senaryosunda
   * ilk POS ödemesi BatchSale ile yapılır, diğerleri için uyarı verilir.
   */
  async processMultiplePayments(
    products: FiscalProductItem[],
    posPayments: MultiplePosPayment[],
    cashAmount?: number,
    showAlertModal?: (title: string, message: string) => void,
    stepCallback?: PaymentStepCallback
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Ingenico 5000F] Çoklu ödeme başlıyor...', {
        productCount: products.length,
        posPaymentCount: posPayments.length,
        cashAmount,
      });

      const items = toGmp3Items(products);
      const totalPosAmount = posPayments.reduce((sum, p) => sum + p.amount, 0);
      const grandTotal = totalPosAmount + (cashAmount || 0);

      if (posPayments.length > 0) {
        stepCallback?.onStepStart?.(0, 'pos', totalPosAmount, 'Kart Ödemesi');

        const result = await callGmp3API('gmp3-batch-sale', {
          items,
          paymentType: 4,
          paymentAmount: toKurus(grandTotal),
          ticketType: 1,
        }, showAlertModal, 120000);

        if (!result.success) {
          if (result.data?.paymentCompleted === true) {
            console.warn('[Ingenico 5000F] Çoklu/POS ödeme hatası döndü ANCAK cihazda ödeme tamamlanmış (GetTicket). Başarılı sayılıyor.');
            stepCallback?.onStepSuccess?.(0, 'pos', totalPosAmount, result.data);
            return {
              success: true,
              documentId: result.data?.transactionHandle || undefined,
              fiscalData: { FiscalTransactionId: result.data?.transactionHandle || null },
            };
          }
          const resolved = await resolveFailureWithVerification(result, 'Çoklu ödeme başarısız.');
          if (resolved.success) {
            stepCallback?.onStepSuccess?.(0, 'pos', totalPosAmount, result.data);
            return {
              success: true,
              documentId: result.data?.transactionHandle || undefined,
              fiscalData: { FiscalTransactionId: result.data?.transactionHandle || null },
            };
          }
          stepCallback?.onStepError?.(0, 'pos', totalPosAmount, resolved.errorMessage || 'POS ödeme başarısız');
          return resolved;
        }

        stepCallback?.onStepSuccess?.(0, 'pos', totalPosAmount, result.data);

        return {
          success: true,
          documentId: result.data?.transactionHandle || undefined,
          fiscalData: {
            FiscalTransactionId: result.data?.transactionHandle || null,
          },
        };
      }

      if (cashAmount && cashAmount > 0) {
        stepCallback?.onStepStart?.(0, 'cash', cashAmount, 'Nakit Ödeme');

        const result = await callGmp3API('gmp3-batch-sale', {
          items,
          paymentType: 1,
          paymentAmount: toKurus(cashAmount),
          ticketType: 1,
        }, showAlertModal, 120000);

        if (!result.success) {
          if (result.data?.paymentCompleted === true) {
            console.warn('[Ingenico 5000F] Çoklu/Nakit ödeme hatası döndü ANCAK cihazda ödeme tamamlanmış (GetTicket). Başarılı sayılıyor.');
            stepCallback?.onStepSuccess?.(0, 'cash', cashAmount, result.data);
            return {
              success: true,
              documentId: result.data?.transactionHandle || undefined,
            };
          }
          const resolvedCash = await resolveFailureWithVerification(result, 'Nakit ödeme başarısız.');
          if (resolvedCash.success) {
            stepCallback?.onStepSuccess?.(0, 'cash', cashAmount, result.data);
            return {
              success: true,
              documentId: result.data?.transactionHandle || undefined,
            };
          }
          stepCallback?.onStepError?.(0, 'cash', cashAmount, resolvedCash.errorMessage || 'Nakit ödeme başarısız');
          return resolvedCash;
        }

        stepCallback?.onStepSuccess?.(0, 'cash', cashAmount, result.data);

        return {
          success: true,
          documentId: result.data?.transactionHandle || undefined,
        };
      }

      return { success: false, errorMessage: 'Ödeme bilgisi bulunamadı.' };
    } catch (error: any) {
      console.error('[Ingenico 5000F] Çoklu ödeme hatası:', error);
      return {
        success: false,
        errorMessage: error.message || 'Çoklu ödeme sırasında beklenmeyen hata.',
      };
    }
  }

  /**
   * Açık işlemi iptal et veya zorla kapat
   * Mali hafızaya yazılmış fişler iptal edilemez (2354), bu durumda fiş kapatılır
   * Ödeme bulunmuşsa (2069) RecoverTransaction ile kurtarma dener
   */
  async cancelDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      const result = await callGmp3API('gmp3-void', {}, undefined, 120000);

      if (result.success) {
        const note = result.data?.note;
        const forceClosed = result.data?.forceClosed;
        if (forceClosed) {
          console.warn('[Ingenico 5000F] Fiş mali hafızaya yazılmış, iptal yerine kapatıldı.');
          showAlertModal?.(
            'Fiş Kapatıldı',
            'Bu fiş mali hafızaya yazılmış olduğu için iptal edilemedi. Bunun yerine fiş kapatıldı. ' +
            (note || '')
          );
        }
        return { success: true };
      }

      // Ödeme bulunmuş (2069) - RecoverTransaction ile kurtarma dene
      if (result.data?.paymentFound || result.data?.errorCode === 2069) {
        console.warn('[Ingenico 5000F] Ödeme bulundu (2069), RecoverTransaction ile kurtarma deneniyor...');
        const recoverResult = await this.recoverTransaction(showAlertModal);
        return recoverResult;
      }

      const friendly = getIngenicoFriendlyError(result.data?.errorCode, result.data?.errorMessage || result.data?.message || '');
      showAlertModal?.(friendly.title, friendly.message);
      return { success: false, errorMessage: friendly.message };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * TransactionFlags bazlı akıllı kurtarma
   * Cihazın durumunu GetTicket ile analiz eder ve en uygun kurtarma adımını uygular:
   * - MF yazılmış → Close
   * - BeforeMF yazılmış → PrintMF + Close
   * - Toplamlar yazılmış → PrintMF + Close
   * - Ödemeler tamamlanmış → PrintTotals + PrintMF + Close
   * - Sadece header → VoidAll + Close
   * - Boş → Close
   */
  async recoverTransaction(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Ingenico 5000F] RecoverTransaction başlıyor...');

      const result = await callGmp3API('gmp3-recover', {}, undefined, 180000);

      if (result.success) {
        const action = result.data?.action || 'unknown';
        const message = result.data?.message || 'Kurtarma tamamlandı.';

        console.log(`[Ingenico 5000F] RecoverTransaction başarılı: action=${action}, message=${message}`);

        if (action === 'partial_payment_pending') {
          const remaining = result.data?.remainingAmount || 0;
          const remainingTL = (remaining / 100).toFixed(2);
          showAlertModal?.(
            'Parçalı Ödeme Bekliyor',
            `Cihazda parçalı ödeme yapılmış bir işlem var. Kalan tutar: ${remainingTL} TL.\nBu işlemi tamamlamak için kalan ödemeyi yapın veya cihazdan manuel iptal edin.`
          );
          return {
            success: false,
            errorMessage: `Parçalı ödeme bekliyor. Kalan: ${remainingTL} TL`,
          };
        }

        return { success: true };
      }

      const friendly = getIngenicoFriendlyError(result.data?.errorCode, result.data?.errorMessage || result.data?.message || '');
      showAlertModal?.(friendly.title, friendly.message);
      return { success: false, errorMessage: friendly.message };
    } catch (error: any) {
      console.error('[Ingenico 5000F] RecoverTransaction hatası:', error);
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Canlı Belge Modu: Belge başlat ve ürünleri ekle (ödeme yapmadan)
   * Start → OptionFlags → TicketHeader → ItemSale (tüm ürünler)
   */
  async startDocumentWithItems(
    products: FiscalProductItem[],
    showAlertModal?: (title: string, message: string) => void,
    totalAmount?: number
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Ingenico 5000F] Belge başlatılıyor...', { productCount: products.length, totalAmount });

      const items = toGmp3Items(products);
      const result = await callGmp3API('gmp3-start-document', {
        items,
        ticketType: 1,
      }, showAlertModal, 120000);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.message || 'Belge başlatılamadı.',
        };
      }

      console.log('[Ingenico 5000F] Belge başlatıldı:', result.data);
      return {
        success: true,
        documentId: result.data?.transactionHandle || undefined,
      };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Canlı Belge Modu: Tek ödeme ekle (açık belgeye)
   * Json_FP3_Payment çağrısı - birden fazla kez çağrılabilir
   */
  async addSinglePayment(
    paymentType: 'pos' | 'cash',
    amount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      const gmp3PaymentType = paymentType === 'pos' ? 'card' : 'cash';
      const amountInKurus = toKurus(amount);

      console.log(`[Ingenico 5000F] Ödeme ekleniyor: ${paymentType} - ${amount} TL (${amountInKurus} kuruş)`);

      const result = await callGmp3API('gmp3-add-payment', {
        paymentType: gmp3PaymentType,
        amount: amountInKurus,
      }, showAlertModal, 120000);

      if (!result.success) {
        // ✅ GetTicket doğrulaması: local-service hata yanıtına cihazdaki gerçek ödeme
        // durumunu (deviceTotalPayment vb.) ekler. HTTP yanıtı hiç alınamadıysa
        // (timeout/ağ hatası) cihaz durumunu buradan sorgula — fişe DOKUNMADAN
        // (parçalı ödeme devam ediyor olabilir, recover ÇAĞRILMAZ).
        let deviceState: FiscalOperationResult['deviceState'];
        if (result.data?.deviceStateVerified === true) {
          deviceState = {
            verified: true,
            totalAmountTL: (result.data.deviceTotalAmount || 0) / 100,
            totalPaymentTL: (result.data.deviceTotalPayment || 0) / 100,
            remainingAmountTL: (result.data.deviceRemainingAmount || 0) / 100,
            paymentsComplete: !!result.data.devicePaymentsComplete,
          };
        } else if (result.rawResponse === null) {
          try {
            const open = await checkIngenicoOpenTransaction();
            if (open) {
              deviceState = {
                verified: true,
                totalAmountTL: open.totalAmountTL,
                totalPaymentTL: open.totalPaymentTL,
                remainingAmountTL: open.remainingAmountTL,
                paymentsComplete: open.paymentsComplete,
              };
            } else {
              deviceState = { verified: false };
            }
          } catch {
            deviceState = { verified: false };
          }
        } else {
          deviceState = { verified: false };
        }

        console.warn('[Ingenico 5000F] Ödeme ekleme hatası. Cihaz durumu:', deviceState);
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.errorMessage || result.data?.message || 'Ödeme eklenemedi.',
          errorCode: result.data?.errorCode,
          deviceState,
        };
      }

      console.log(`[Ingenico 5000F] Ödeme eklendi: ${paymentType} - ${amount} TL`);
      return { success: true };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Canlı Belge Modu: Belgeyi kapat
   * PrintTotalsAndPayments → PrintBeforeMF → PrintMF → Close
   */
  async closeDocument(
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log('[Ingenico 5000F] Belge kapatılıyor...');

      const result = await callGmp3API('gmp3-close-document', {}, showAlertModal, 120000);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.errorMessage || result.data?.message || 'Belge kapatılamadı.',
        };
      }

      console.log('[Ingenico 5000F] Belge kapatıldı.');
      return { success: true };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Tek urun iptali (acik fis icinde, odeme oncesi)
   * VO01: Satilmis bir urunu iptal eder
   * VO02: Zaten iptal edilmis urun tekrar iptal edilmeye calisilirsa cihaz hata doner
   */
  async voidItem(
    itemIndex: number,
    itemCount: number = 1,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      console.log(`[Ingenico 5000F] Ürün iptali: index=${itemIndex}, count=${itemCount}`);

      const result = await callGmp3API('gmp3-void-item', {
        itemIndex,
        itemCount,
        itemCountPrecision: 0,
      }, showAlertModal, 120000);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.errorMessage || result.data?.message || 'Ürün iptal edilemedi.',
        };
      }

      console.log(`[Ingenico 5000F] Ürün iptal edildi: index=${itemIndex}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Odeme iptali (acik fis icinde, parcali odeme sirasinda)
   * VO03: Nakit odeme iptali (reverseType='cash')
   * VO04: Kredili odeme iptali (reverseType='card_void')
   */
  async reversePayment(
    reverseType: 'cash' | 'card_void' | 'card_refund',
    amount: number,
    showAlertModal?: (title: string, message: string) => void
  ): Promise<FiscalOperationResult> {
    try {
      const amountInKurus = toKurus(amount);
      console.log(`[Ingenico 5000F] Ödeme iptali: type=${reverseType}, amount=${amount} TL (${amountInKurus} kuruş)`);

      const result = await callGmp3API('gmp3-reverse-payment', {
        reverseType,
        amount: amountInKurus,
      }, showAlertModal, 120000);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.errorMessage || result.data?.message || 'Ödeme iptal edilemedi.',
        };
      }

      console.log(`[Ingenico 5000F] Ödeme iptal edildi: type=${reverseType}, amount=${amount} TL`);
      return { success: true };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Nakit odemesini iptal et (parcali odeme sirasinda, acik fis icinde)
   * VO03 senaryosu: Json_FP3_VoidPayment ile index bazli iptal
   */
  async refundCashPayment(
    paymentSequenceNo: number,
    showAlertModal?: (title: string, message: string) => void,
    amount?: number
  ): Promise<FiscalOperationResult> {
    try {
      const paymentIndex = (paymentSequenceNo || 1) - 1;
      console.log(`[Ingenico 5000F] 💵 Nakit ödeme iptal ediliyor... Sıra No: ${paymentSequenceNo}, Index: ${paymentIndex}`);

      const result = await callGmp3API('gmp3-void-payment', {
        paymentIndex,
      }, showAlertModal, 120000);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.errorMessage || result.data?.message || 'Nakit ödeme iptal edilemedi.',
        };
      }

      console.log(`[Ingenico 5000F] Nakit ödeme iptal edildi: index=${paymentIndex}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * POS odemesini iptal et (parcali odeme sirasinda, acik fis icinde)
   * VO04 senaryosu: Json_FP3_VoidPayment ile index bazli iptal
   */
  async refundPosPayment(
    paymentSequenceNo: number,
    showAlertModal?: (title: string, message: string) => void,
    fiscalData?: any,
    amount?: number
  ): Promise<FiscalOperationResult> {
    try {
      const paymentIndex = (paymentSequenceNo || 1) - 1;
      console.log(`[Ingenico 5000F] 💳 POS ödeme iptal ediliyor... Sıra No: ${paymentSequenceNo}, Index: ${paymentIndex}`);

      const result = await callGmp3API('gmp3-void-payment', {
        paymentIndex,
      }, showAlertModal, 120000);

      if (!result.success) {
        return {
          success: false,
          errorMessage: result.data?.friendlyMessage || result.data?.errorMessage || result.data?.message || 'POS ödeme iptal edilemedi.',
        };
      }

      console.log(`[Ingenico 5000F] POS ödeme iptal edildi: index=${paymentIndex}`);
      return { success: true };
    } catch (error: any) {
      return { success: false, errorMessage: error.message };
    }
  }

  /**
   * Bağlantı kontrolü - Echo komutu ile
   * Echo başarısızsa (örn. Local Service yeniden başladı, eşleştirme düştü)
   * otomatik yeniden bağlanma + eşleştirme denenir ve Echo tekrarlanır.
   * Cihazda açık işlem varsa otomatik iptal eder
   */
  async checkConnection(): Promise<boolean> {
    try {
      let result = await callGmp3API('gmp3-echo', {}, undefined, 15000);
      if (!result.success) {
        // Interface/eşleştirme düşmüş olabilir: reconnect (broker hafızası,
        // olmazsa kurulum kaydındaki IP + ortak eşleştirme kimliği) ve tek retry.
        console.warn('[Ingenico 5000F] Echo başarısız, otomatik yeniden bağlanma deneniyor...');
        const reconnected = await attemptAutoReconnect();
        if (!reconnected) return false;
        result = await callGmp3API('gmp3-echo', {}, undefined, 15000);
        if (!result.success) return false;
      }

      // Echo yanitindan acik islem kontrolu - RecoverTransaction ile akilli kurtarma
      try {
        const echoData = result.data?.response
          ? (typeof result.data.response === 'string' ? JSON.parse(result.data.response) : result.data.response)
          : result.data;

        if (echoData?.Handle && echoData.Handle !== '0' && echoData.Handle !== 0) {
          console.warn('[Ingenico 5000F] Cihazda yarı kalmış işlem tespit edildi (Handle:', echoData.Handle, '). RecoverTransaction ile kurtarılıyor...');
          const recoverResult = await callGmp3API('gmp3-recover', {}, undefined, 180000);
          if (recoverResult.success) {
            const action = recoverResult.data?.action || 'kurtarıldı';
            console.log(`[Ingenico 5000F] Yarı kalmış işlem kurtarıldı: ${action} - ${recoverResult.data?.message || ''}`);
          } else {
            console.warn('[Ingenico 5000F] Yarı kalmış işlem kurtarılamadı:', recoverResult.data?.message || recoverResult.data?.errorMessage);
          }
        }
      } catch {
        // Echo parse hatasi kritik degil
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Cihaz bilgilerini al
   */
  async getDeviceInfo(): Promise<any> {
    try {
      const result = await callGmp3API('gmp3-status', {}, undefined, 10000);
      return result.data;
    } catch {
      return null;
    }
  }
}

export const ingenico5000fDevice = new Ingenico5000FDevice();

export interface IngenicoTicketItem {
  name: string;
  unitPriceKurus: number;
  unitPriceTL: number;
  amountTL: number; // toplam (unitPrice * quantity)
  quantity: number; // x1000 formatindan donusturulmus
  deptIndex: number;
}

export interface IngenicoOpenTransactionData {
  hasOpenTransaction: boolean;
  transactionHandle?: string;
  totalAmount: number; // kurus cinsinden
  totalPayment: number; // kurus cinsinden
  remainingAmount: number; // kurus cinsinden
  totalAmountTL: number;
  totalPaymentTL: number;
  remainingAmountTL: number;
  paymentsComplete: boolean;
  headerPrinted: boolean;
  transactionFlags: number;
  ticket?: string;
  items: IngenicoTicketItem[];
}

export async function checkIngenicoOpenTransaction(): Promise<IngenicoOpenTransactionData | null> {
  try {
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-check-open-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(30000),
    });
    const data = await resp.json();

    if (!data.success || !data.hasOpenTransaction) {
      return null;
    }

    // Ticket JSON'dan urunleri parse et (GetTicket "SaleInfo" dizisinde tutar)
    let items: IngenicoTicketItem[] = [];
    try {
      if (data.ticket) {
        const ticketObj = typeof data.ticket === 'string' ? JSON.parse(data.ticket) : data.ticket;
        const saleInfo = ticketObj?.SaleInfo || ticketObj?.stItems;
        if (saleInfo && Array.isArray(saleInfo)) {
          items = saleInfo
            .filter((it: any) => it && (it.ItemPrice > 0 || it.amount > 0) && (it.Name || it.name))
            .map((it: any) => {
              const unitPriceKurus = it.ItemPrice || it.amount || 0;
              const qty = Math.max(1, Math.round((it.ItemCount || it.count || 1000) / 1000));
              return {
                name: ((it.Name || it.name) || '').trim(),
                unitPriceKurus,
                unitPriceTL: unitPriceKurus / 100,
                amountTL: (unitPriceKurus * qty) / 100,
                quantity: qty,
                deptIndex: it.DeptIndex ?? it.deptIndex ?? 1,
              };
            });
          console.log('[Ingenico 5000F] Ticket urunleri parse edildi:', items.length, 'urun', items);
        }
      }
    } catch (parseErr) {
      console.warn('[Ingenico 5000F] Ticket item parse hatasi:', parseErr);
    }

    return {
      hasOpenTransaction: true,
      transactionHandle: data.transactionHandle,
      totalAmount: data.totalAmount || 0,
      totalPayment: data.totalPayment || 0,
      remainingAmount: data.remainingAmount || 0,
      totalAmountTL: (data.totalAmount || 0) / 100,
      totalPaymentTL: (data.totalPayment || 0) / 100,
      remainingAmountTL: (data.remainingAmount || 0) / 100,
      paymentsComplete: data.paymentsComplete || false,
      headerPrinted: data.headerPrinted || false,
      transactionFlags: data.transactionFlags || 0,
      ticket: data.ticket,
      items,
    };
  } catch (err: any) {
    console.error('[Ingenico 5000F] Acik islem kontrolu hatasi:', err.message);
    return null;
  }
}

export interface IngenicoCancelResult {
  success: boolean;
  /** clean | voided_clean | payments_complete_close | partial_payment_pending | open_unreadable | void_failed */
  action?: string;
  message?: string;
  totalAmountTL?: number;
  totalPaymentTL?: number;
}

export async function cancelIngenicoOpenTransaction(): Promise<IngenicoCancelResult> {
  try {
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-recover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(180000),
    });
    const data = await resp.json();
    console.log('[Ingenico 5000F] Acik islem iptal sonucu:', data?.action, data?.message);
    return {
      success: data.success === true,
      action: data.action,
      message: data.message,
      totalAmountTL: (data.totalAmount || 0) / 100,
      totalPaymentTL: (data.totalPayment || 0) / 100,
    };
  } catch (err: any) {
    console.error('[Ingenico 5000F] Acik islem iptal hatasi:', err.message);
    return { success: false, message: err.message };
  }
}

// ─────────────────────────────────────────────────────────────
// Departman & KDV Yönetimi (Dynamic VAT Table)
// ─────────────────────────────────────────────────────────────

export interface IngenicoDepartment {
  szDeptName: string;
  u8TaxIndex: number;
  iCurrencyType: number;
  iUnitType: number;
  u64Limit: number;
  u64Price: number;
  bLuchVoucher: number;
}

export interface IngenicoTaxRate {
  taxRate: number; // yuzde * 100 (ornegin %10 = 1000)
}

export interface DepartmentVatInfo {
  deptIndex: number;
  deptName: string;
  taxIndex: number;
  taxRatePercent: number; // orn: 10, 20, 1, 0
}

export interface DepartmentVatMismatch {
  deptIndex: number;
  deptName: string;
  deviceTaxIndex: number;
  deviceVatPercent: number;
  expectedVatPercent: number;
  expectedTaxIndex: number;
  affectedProducts: string[];
}

export interface DepartmentVatSyncResult {
  success: boolean;
  departments: DepartmentVatInfo[];
  taxRates: { index: number; percent: number }[];
  mismatches: DepartmentVatMismatch[];
  hasMismatches: boolean;
}

let cachedDeptVatSyncResult: DepartmentVatSyncResult | null = null;

/**
 * Cihazdan okunan departman/KDV tablosu onbellegi. Departman tablosu satis
 * sirasinda degismedigi icin her satista cihaza GetDepartments+GetTaxRates
 * gondermeye gerek yok; bu cagrilar satisi saniyelerce uzatiyor ve cihaz
 * mesgulken 2309/61443 hatalari uretiyordu.
 */
const DEPT_INFO_CACHE_TTL_MS = 10 * 60 * 1000;
let cachedDeviceDeptInfo: {
  info: Awaited<ReturnType<typeof getDepartmentsAndTaxRates>>;
  fetchedAt: number;
} | null = null;

export function getCachedDeptVatSync(): DepartmentVatSyncResult | null {
  return cachedDeptVatSyncResult;
}

export async function getIngenicoDepartments(): Promise<{ success: boolean; departments: IngenicoDepartment[]; error?: string }> {
  try {
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-get-departments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(15000),
    });
    const data = await resp.json();
    if (!data.success) {
      return { success: false, departments: [], error: data.errorMessage || 'GetDepartments basarisiz' };
    }
    const deptStr = data.data?.departments || data.departments || '';
    let departments: IngenicoDepartment[] = [];
    try {
      departments = typeof deptStr === 'string' ? JSON.parse(deptStr) : deptStr;
    } catch { /* parse error */ }
    return { success: true, departments };
  } catch (err: any) {
    return { success: false, departments: [], error: err.message };
  }
}

export async function getIngenicoTaxRates(): Promise<{ success: boolean; taxRates: IngenicoTaxRate[]; error?: string }> {
  try {
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-get-tax-rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(15000),
    });
    const data = await resp.json();
    if (!data.success) {
      return { success: false, taxRates: [], error: data.errorMessage || 'GetTaxRates basarisiz' };
    }
    const rateStr = data.data?.taxRates || data.taxRates || '';
    let taxRates: IngenicoTaxRate[] = [];
    try {
      taxRates = typeof rateStr === 'string' ? JSON.parse(rateStr) : rateStr;
    } catch { /* parse error */ }
    return { success: true, taxRates };
  } catch (err: any) {
    return { success: false, taxRates: [], error: err.message };
  }
}

export async function setIngenicoDepartments(departments: IngenicoDepartment[], password: string): Promise<{ success: boolean; error?: string }> {
  try {
    const resp = await fetchWithFallback(`${getApiBaseUrl()}/gmp3-set-departments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        departments: JSON.stringify(departments),
        password,
        numberOfDepartments: departments.length,
      }),
      signal: AbortSignal.timeout(30000),
    });
    const data = await resp.json();
    if (!data.success) {
      return { success: false, error: data.data?.errorMessage || data.errorMessage || 'SetDepartments basarisiz' };
    }
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

export async function getDepartmentsAndTaxRates(): Promise<{
  success: boolean;
  departments: DepartmentVatInfo[];
  taxRates: { index: number; percent: number }[];
  rawDepartments: IngenicoDepartment[];
  error?: string;
}> {
  const [deptResult, taxResult] = await Promise.all([
    getIngenicoDepartments(),
    getIngenicoTaxRates(),
  ]);

  if (!deptResult.success) {
    return { success: false, departments: [], taxRates: [], rawDepartments: [], error: deptResult.error };
  }
  if (!taxResult.success) {
    return { success: false, departments: [], taxRates: [], rawDepartments: [], error: taxResult.error };
  }

  // Ingenico taxRate: %10 için 1000 (yüzde*100) veya 10 olarak gelebilir
  const taxRates = taxResult.taxRates.map((t, i) => {
    const raw = t.taxRate || 0;
    const percent = raw >= 100 ? raw / 100 : raw;
    return { index: i, percent };
  });

  const departments = deptResult.departments.map((d, i) => {
    const taxEntry = taxRates.find(t => t.index === d.u8TaxIndex);
    return {
      deptIndex: i + 1,
      deptName: d.szDeptName || `Dept ${i + 1}`,
      taxIndex: d.u8TaxIndex,
      taxRatePercent: taxEntry?.percent ?? 0,
    };
  });

  return { success: true, departments, taxRates, rawDepartments: deptResult.departments };
}

/**
 * GTS urunlerinin departman-KDV eslesmesini Ingenico cihaziyla karsilastirir.
 * Uyumsuzluk varsa detayli bilgi doner.
 * products: { departmentNo, vatRate, name } listesi
 */
export async function syncDepartmentVatMapping(
  products: { departmentNo: number; vatRate: number; name: string }[]
): Promise<DepartmentVatSyncResult> {
  const emptyResult: DepartmentVatSyncResult = {
    success: false,
    departments: [],
    taxRates: [],
    mismatches: [],
    hasMismatches: false,
  };

  // Cihazdaki departman tablosu satis sirasinda degismez; her satista
  // GetDepartments+GetTaxRates gondermek hem satisi uzatir hem de cihaz
  // mesgulken 2309/61443 hatalari uretir. TTL suresince onbellekten okunur,
  // urun listesiyle karsilastirma her satista yerelde yapilir.
  // ONEMLI: Son kontrolde uyumsuzluk bulunduysa onbellek ATLANIR - kurulum/
  // duzeltme surecinde tablo sik degisir (cihaz menusunden elle bile
  // duzeltilmis olabilir); uyumsuzluk cozulene kadar hep taze veri okunur.
  const lastSyncHadMismatches = cachedDeptVatSyncResult?.hasMismatches === true;
  let info: Awaited<ReturnType<typeof getDepartmentsAndTaxRates>>;
  const now = Date.now();
  if (
    !lastSyncHadMismatches &&
    cachedDeviceDeptInfo &&
    now - cachedDeviceDeptInfo.fetchedAt < DEPT_INFO_CACHE_TTL_MS
  ) {
    info = cachedDeviceDeptInfo.info;
  } else {
    info = await getDepartmentsAndTaxRates();
    if (info.success) {
      cachedDeviceDeptInfo = { info, fetchedAt: now };
    }
  }

  if (!info.success) {
    console.warn('[Ingenico 5000F] Departman/KDV bilgisi alinamadi:', info.error);
    return emptyResult;
  }

  const deptProductMap: Record<number, { vatRate: number; names: string[] }> = {};
  for (const p of products) {
    if (!p.departmentNo || p.departmentNo < 1) continue;
    if (!deptProductMap[p.departmentNo]) {
      deptProductMap[p.departmentNo] = { vatRate: p.vatRate, names: [] };
    }
    if (!deptProductMap[p.departmentNo].names.includes(p.name)) {
      deptProductMap[p.departmentNo].names.push(p.name);
    }
  }

  const mismatches: DepartmentVatMismatch[] = [];

  for (const [deptNoStr, productInfo] of Object.entries(deptProductMap)) {
    const deptNo = parseInt(deptNoStr);
    const dept = info.departments.find(d => d.deptIndex === deptNo);
    if (!dept) continue;

    const expectedTaxIndex = info.taxRates.find(t => Math.abs(t.percent - productInfo.vatRate) < 0.01);
    if (!expectedTaxIndex) {
      console.warn(`[Ingenico 5000F] KDV orani %${productInfo.vatRate} cihazdaki KDV tablosunda bulunamadi`);
      continue;
    }

    if (dept.taxIndex !== expectedTaxIndex.index) {
      mismatches.push({
        deptIndex: deptNo,
        deptName: dept.deptName,
        deviceTaxIndex: dept.taxIndex,
        deviceVatPercent: dept.taxRatePercent,
        expectedVatPercent: productInfo.vatRate,
        expectedTaxIndex: expectedTaxIndex.index,
        affectedProducts: productInfo.names,
      });
    }
  }

  const result: DepartmentVatSyncResult = {
    success: true,
    departments: info.departments,
    taxRates: info.taxRates,
    mismatches,
    hasMismatches: mismatches.length > 0,
  };

  cachedDeptVatSyncResult = result;

  if (mismatches.length > 0) {
    console.warn('[Ingenico 5000F] Departman-KDV uyumsuzluklari:', mismatches);
  } else {
    console.log('[Ingenico 5000F] Departman-KDV eslesmesi dogru.');
  }

  return result;
}

/**
 * Uyumsuzluklari SetDepartments ile cihazda duzeltir.
 * rawDepartments: cihazdaki mevcut departman ayarlari (GetDepartments'dan)
 * mismatches: duzeltilmesi gereken uyumsuzluklar
 * password: supervisor password
 */
export async function fixDepartmentMismatches(
  mismatches: DepartmentVatMismatch[],
  password: string
): Promise<{ success: boolean; error?: string }> {
  const info = await getDepartmentsAndTaxRates();
  if (!info.success) {
    return { success: false, error: 'Cihazdaki departman bilgisi alinamadi' };
  }

  const updatedDepts = [...info.rawDepartments];
  for (const m of mismatches) {
    const idx = m.deptIndex - 1;
    if (idx >= 0 && idx < updatedDepts.length) {
      updatedDepts[idx].u8TaxIndex = m.expectedTaxIndex;
    }
  }

  console.log('[Ingenico 5000F] SetDepartments cagriliyor, guncellenen departmanlar:', mismatches.map(m => `Dept${m.deptIndex}: taxIndex ${m.deviceTaxIndex} -> ${m.expectedTaxIndex}`));
  const result = await setIngenicoDepartments(updatedDepts, password);

  if (result.success) {
    cachedDeptVatSyncResult = null;
    // Cihazdaki tablo degisti; onbellek gecersiz.
    cachedDeviceDeptInfo = null;
  }

  return result;
}
